import { fetchConfig } from '@theme/utilities';
import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

/**
 * <bigens-mix-match> (T42, mockup E): the mix & match page, sections/bigens-mix-match.liquid.
 *
 * Owns the set on this page: family tabs → one card per size (price as it will be in the current set) → the shared
 * <bigens-set-tray> (T41b) draws the slots, tier bar and nudge and emits add / change / remove; this element keeps the
 * model, the tier maths (the same as the PDP builder: savings rounded down per line, per-ball prices rounded up), the
 * line items, the pump toggle (never counts, D19), the curated sets and the cart post (/cart/add.js with the builder's
 * line properties, announced with CartLinesUpdateEvent so the cart drawer opens and re-renders like product-form.js).
 *
 * Set state is shared with the PDP through the tray's store (localStorage `bigens:set`, source 'page').
 * Every string comes from the section's `data-mix-strings` JSON (locales `bigens.mix.*` + `bigens.deal_builder.*`).
 *
 * @typedef {{ v: string, label: string, note?: string, hex?: string }} OptionValue
 * @typedef {{ name: string, kind: 'size' | 'color' | 'other', values: OptionValue[] }} ProductOption
 * @typedef {{ id: number, o: string[], p: number, a: boolean, s: string, d?: string, dl?: string, n?: string, img?: string, size?: string, cm?: number, in?: number, c?: string, url?: string }} Variant
 * @typedef {{ id: number, family: string, name: string, short?: string, legacy?: boolean, options: ProductOption[], variants: Variant[] }} Product
 * @typedef {{ key: string, name: string, short: string, products: number[] }} Family
 * @typedef {{ key: string, cm?: number, in?: number, who: string, name: string, legacy?: boolean, img?: string, variants: Array<{ product: Product, variant: Variant }> }} SizeGroup
 */

// T48 (D55): 'legacy' is the tab of the old 50″ (another manufacturer): a ball that counts in the set, never a Play Ball.
const BALL_FAMILIES = ['play', 'legacy', 'hoppi', 'animal-hopper'];
const SET_MAX_AGE = 7 * 24 * 60 * 60 * 1000;
const BALL_MIN = 34;
const BALL_MAX = 64;

class BigensMixMatch extends HTMLElement {
  /** @type {Map<string, Product>} */
  products = new Map();

  /** @type {Map<string, { product: Product, variant: Variant }>} */
  variants = new Map();

  /** @type {Array<{ variantId: string }>} */
  lines = [];

  /** @type {{ id: string, p: number, name: string } | null} the pump in the set (never counts toward the tiers, D19) */
  pump = null;

  /** @type {Map<string, string>} card key → the variant picked on that card */
  selection = new Map();

  /** @type {number | null} the slot being replaced after a tap on a filled tray slot */
  replaceIndex = null;

  activeFamily = '';
  trayVisible = true;

  /** @type {Record<string, string>} */
  strings = {};

  #mobile = window.matchMedia('(max-width: 989px)');

  connectedCallback() {
    if (this.initialized || this.pendingInit) return;
    this.pendingInit = true;
    customElements.whenDefined('bigens-set-tray').then(() => {
      this.pendingInit = false;
      if (this.isConnected) this.init();
    });
  }

  init() {
    try {
      this.data = JSON.parse(this.querySelector('[data-mix-data]')?.textContent || '{}');
      this.strings = JSON.parse(this.querySelector('[data-mix-strings]')?.textContent || '{}');
    } catch (error) {
      console.error('[mix & match] Invalid data.', error);
      return;
    }

    (this.data.products || []).forEach((/** @type {Product} */ product) => this.addProduct(product));
    /** @type {Family[]} */
    this.families = Array.isArray(this.data.families) ? this.data.families : [];
    this.tiers = Array.isArray(this.data.tiers) ? this.data.tiers.map(Number) : [0, 0, 0, 0];
    this.maxItems = Math.max(4, Number(this.data.max) || 12);
    this.sizes = this.data.sizes || {};

    this.tray = /** @type {any} */ (this.querySelector('bigens-set-tray'));
    this.store = /** @type {any} */ (customElements.get('bigens-set-tray'))?.store;
    this.trayBox = this.querySelector('[data-mix-tray]');
    this.bar = this.querySelector('[data-mix-bar]');

    const selectedTab = this.querySelector('[data-mix-tab][aria-selected="true"]');
    this.activeFamily = selectedTab?.getAttribute('data-mix-tab') || this.families[0]?.key || '';
    // T48: the page may open on a later tab (the first one with something to buy); keep it in view in the tab row.
    const tabRow = this.querySelector('[data-mix-tabs]');
    if (tabRow instanceof HTMLElement && selectedTab instanceof HTMLElement && tabRow.scrollWidth > tabRow.clientWidth) {
      const offset = selectedTab.getBoundingClientRect().left - tabRow.getBoundingClientRect().left;
      tabRow.scrollLeft = Math.max(0, tabRow.scrollLeft + offset - 16);
    }

    this.restoreSet();

    this.addEventListener('click', this.handleClick);
    this.addEventListener('change', this.handleChange);
    this.querySelector('[data-mix-tabs]')?.addEventListener('keydown', this.handleTabKeys);
    this.addEventListener('bigens-set-tray:add', () => this.goToCards());
    this.addEventListener('bigens-set-tray:change', (event) => this.startReplace(/** @type {any} */ (event).detail.index));
    this.addEventListener('bigens-set-tray:remove', (event) => this.removeRow(/** @type {any} */ (event).detail.index));

    if (this.trayBox) {
      this.trayObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;
          this.trayVisible = entry.isIntersecting;
          this.updateBar();
        },
        { threshold: 0.15 },
      );
      this.trayObserver.observe(this.trayBox);
    }
    this.#mobile.addEventListener('change', this.updateBar);

    this.initialized = true;
    this.renderCards();
    this.renderCombos();
    this.render();
  }

  disconnectedCallback() {
    this.trayObserver?.disconnect();
    this.#mobile.removeEventListener('change', this.updateBar);
    clearTimeout(this.addedTimer);
  }

  /**
   * @param {Product} product
   * @param {boolean} [replace]
   */
  addProduct(product, replace = false) {
    const key = String(product.id);
    if (this.products.has(key) && !replace) return;
    this.products.set(key, product);
    product.variants.forEach((variant) => this.variants.set(String(variant.id), { product, variant }));
  }

  /** Re-draws everything from the current data (used after products are added at runtime). */
  refresh() {
    if (!this.initialized) return;
    this.renderCards();
    this.renderCombos();
    this.render();
  }

  /* ---------- Model ---------- */

  /** @param {{ variantId: string } | null | undefined} line */
  resolve(line) {
    return line?.variantId ? this.variants.get(line.variantId) : undefined;
  }

  /** @param {Variant | undefined} variant */
  isOffered(variant) {
    return Boolean(variant && variant.a && variant.s !== 'waitlist');
  }

  /** @param {number} count */
  percentFor(count) {
    if (count < 2) return 0;
    return this.tiers[Math.min(count, 4) - 1] || 0;
  }

  /**
   * Totals for a list of balls (plus the pump when asked). Savings are rounded down per ball and multiplied, like
   * Shopify's per-item allocation, so the shown total matches the cart to the cent (the builder's rule).
   * @param {Array<Variant | undefined>} balls
   * @param {boolean} [withPump]
   */
  totals(balls, withPump = false) {
    const percent = this.percentFor(balls.length);
    /** @type {Map<number, { price: number, quantity: number }>} */
    const grouped = new Map();
    balls.forEach((variant) => {
      if (!variant) return;
      const entry = grouped.get(variant.id) || { price: Number(variant.p) || 0, quantity: 0 };
      entry.quantity += 1;
      grouped.set(variant.id, entry);
    });

    let subtotal = 0;
    let saving = 0;
    grouped.forEach(({ price, quantity }) => {
      subtotal += price * quantity;
      saving += Math.floor((price * percent) / 100) * quantity;
    });

    const pump = withPump && this.pump ? Number(this.pump.p) || 0 : 0;
    return { percent, saving, total: subtotal - saving + pump };
  }

  /** The balls in the set as variants. */
  balls() {
    return this.lines.map((line) => this.resolve(line)?.variant);
  }

  /**
   * One ball's price per line at a given percentage (never understated: the saving is floored).
   * @param {Variant} variant
   * @param {number} percent
   */
  discounted(variant, percent) {
    const price = Number(variant.p) || 0;
    return price - Math.floor((price * percent) / 100);
  }

  /** @param {Product} product */
  colorIndex(product) {
    return product.options.findIndex((option) => option.kind === 'color');
  }

  /**
   * @param {Product} product
   * @param {Variant} variant
   */
  colorOf(product, variant) {
    const index = this.colorIndex(product);
    if (index >= 0) {
      const value = product.options[index].values.find((candidate) => candidate.v === variant.o[index]);
      return { name: variant.o[index] || '', hex: value?.hex };
    }
    // T45 (AUD2 #1): a product without a colour option names its colour in `bigens.color` (variant `c`); blank = no
    // colour at all. Never guessed from the family colour.
    const name = String(variant.c || '').trim();
    return { name, hex: name ? this.hexFor(name) : undefined };
  }

  /**
   * The swatch colour for a colour name: the same name on any product's colour option, else a CSS colour keyword.
   * @param {string} name
   */
  hexFor(name) {
    const wanted = name.toLowerCase();
    for (const product of this.products.values()) {
      const index = this.colorIndex(product);
      if (index < 0) continue;
      const value = product.options[index].values.find((candidate) => candidate.v.trim().toLowerCase() === wanted && candidate.hex);
      if (value?.hex) return value.hex;
    }
    return window.CSS?.supports?.('color', wanted) ? wanted : undefined;
  }

  /**
   * "50 in" (EN) / "127 cm" (nl, de, fr): from the size map, else the product's size option label.
   * @param {Product} product
   * @param {Variant} variant
   */
  sizeOf(product, variant) {
    const size = this.sizes[String(variant.id)] || (variant.cm && variant.in ? { cm: variant.cm, in: variant.in } : null);
    if (size?.cm && size?.in) return this.text('sizeShort', { in: size.in, cm: size.cm });
    const index = product.options.findIndex((option) => option.kind === 'size');
    if (index >= 0) {
      return product.options[index].values.find((value) => value.v === variant.o[index])?.label || variant.o[index];
    }
    return variant.size || '';
  }

  /**
   * The tab a product lives on: its family, or 'legacy' for the legacy ball (T48).
   * @param {Product} product
   */
  tabOf(product) {
    return product.legacy ? 'legacy' : product.family;
  }

  /**
   * The size as a name part: blank for a legacy product, whose title already names it ("… Ball, 50 in", T48).
   * @param {Product} product
   * @param {Variant} variant
   */
  nameSize(product, variant) {
    return product.legacy ? '' : this.sizeOf(product, variant);
  }

  /** @param {string} family */
  familyOf(family) {
    return this.families.find((candidate) => candidate.key === family);
  }

  /**
   * The cards of one family: one group per size (cm), unknown sizes as one card per product.
   * @param {string} family
   * @returns {SizeGroup[]}
   */
  sizesFor(family) {
    const entry = this.familyOf(family);
    if (!entry) return [];
    /** @type {Map<string, SizeGroup>} */
    const groups = new Map();
    entry.products.forEach((productId) => {
      const product = this.products.get(String(productId));
      if (!product) return;
      product.variants.forEach((variant) => {
        const size = this.sizes[String(variant.id)];
        // T48: a legacy product is always its own card (its title, its photo), never grouped by size.
        const key = size?.cm && !product.legacy ? `${family}-${size.cm}` : `${family}-p${product.id}`;
        const group = groups.get(key) || {
          key,
          cm: size?.cm,
          in: size?.in,
          who: size?.who || '',
          name: product.name,
          legacy: Boolean(product.legacy),
          img: product.legacy ? variant.img : undefined,
          variants: [],
        };
        if (!group.who && size?.who) group.who = size.who;
        group.variants.push({ product, variant });
        groups.set(key, group);
      });
    });
    return Array.from(groups.values()).sort((a, b) => (a.cm || Infinity) - (b.cm || Infinity));
  }

  /** The cm range over every ball family, for drawing the balls to one scale. */
  scaleRange() {
    const values = Object.values(this.sizes)
      .map((size) => Number(/** @type {any} */ (size)?.cm) || 0)
      .filter((cm) => cm > 0);
    return { min: Math.min(...values), max: Math.max(...values), any: values.length > 0 };
  }

  /** @param {number | undefined} cm */
  ballSize(cm) {
    const range = this.scaleRange();
    if (!cm || !range.any || range.max === range.min) return 54;
    return Math.round(BALL_MIN + ((cm - range.min) / (range.max - range.min)) * (BALL_MAX - BALL_MIN));
  }

  /**
   * One swatch per colour on a size card (T44 → T45). A size can hold several products (the legacy 50″ listing and the
   * Play Ball's 127 cm): variants join one swatch only when they carry the same colour name (option value or
   * `bigens.color`). A ball without any colour name gets its own swatch (its photo, no name). Per swatch the buyable
   * variant wins.
   * @param {SizeGroup} group
   * @returns {Array<{ product: Product, variant: Variant, label: string, color: { name: string, hex?: string }, variants: Variant[] }>}
   */
  swatchesFor(group) {
    /** @type {Map<string, { product: Product, variant: Variant, label: string, color: { name: string, hex?: string }, variants: Variant[] }>} */
    const byColor = new Map();
    group.variants.forEach(({ product, variant }) => {
      const color = this.colorOf(product, variant);
      const key = color.name ? color.name.toLowerCase() : `product-${product.id}-${variant.id}`;
      const swatch = byColor.get(key);
      if (!swatch) {
        byColor.set(key, { product, variant, label: color.name || product.name, color, variants: [variant] });
        return;
      }
      swatch.variants.push(variant);
      if (!swatch.color.hex && color.hex) swatch.color = color;
      if (!this.isOffered(swatch.variant) && this.isOffered(variant)) {
        swatch.product = product;
        swatch.variant = variant;
      }
    });
    return Array.from(byColor.values());
  }

  /**
   * The card's big ball: the picked colour while the size can be bought, the family colour on a sold-out size.
   * @param {SizeGroup} group
   * @param {string | undefined} hex
   */
  cardBallColor(group, hex) {
    const buyable = group.variants.some(({ variant }) => this.isOffered(variant));
    return (buyable && hex) || familyColor(this.activeFamily);
  }

  /** @param {SizeGroup} group */
  selectedIn(group) {
    const picked = this.selection.get(group.key);
    if (picked && group.variants.some(({ variant }) => String(variant.id) === picked)) return picked;
    const offered = group.variants.find(({ variant }) => this.isOffered(variant)) || group.variants[0];
    return offered ? String(offered.variant.id) : '';
  }

  /** The offered variants of a family, the preferred size (cm) first. @param {string} family @param {number} [preferCm] */
  offeredIn(family, preferCm) {
    const entry = this.familyOf(family);
    if (!entry) return [];
    /** @type {Variant[]} */
    const offered = [];
    entry.products.forEach((productId) => {
      const product = this.products.get(String(productId));
      product?.variants.forEach((variant) => {
        if (this.isOffered(variant)) offered.push(variant);
      });
    });
    if (!preferCm) return offered;
    const preferred = offered.filter((variant) => this.sizes[String(variant.id)]?.cm === preferCm);
    return preferred.length > 0 ? preferred : offered;
  }

  restoreSet() {
    try {
      const saved = this.store?.read?.();
      if (!saved || !Array.isArray(saved.lines)) return;
      const age = Date.now() - Date.parse(saved.updatedAt || '');
      if (!(age >= 0 && age < SET_MAX_AGE)) return;
      // Anything that isn't a { variantId } object (hand-edited or corrupt storage) is skipped.
      this.lines = saved.lines
        .filter((line) => line && typeof line === 'object' && line.variantId)
        .map((line) => String(line.variantId))
        .filter((variantId) => this.isOffered(this.variants.get(variantId)?.variant))
        .slice(0, this.maxItems)
        .map((variantId) => ({ variantId }));
    } catch {
      this.lines = [];
    }
  }

  saveSet() {
    if (!this.store) return;
    if (this.lines.length === 0) {
      this.store.clear?.();
      return;
    }
    const lines = [];
    for (const line of this.lines) {
      const selection = this.resolve(line);
      if (selection) lines.push({ productId: String(selection.product.id), variantId: String(selection.variant.id) });
    }
    this.store.write?.(lines, 'page');
  }

  /* ---------- Events ---------- */

  /** @param {MouseEvent} event */
  handleClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('button');
    if (!button || !this.contains(button) || button.disabled) return;

    if (button.matches('[data-mix-tab]')) {
      this.selectTab(button.getAttribute('data-mix-tab') || '', false);
    } else if (button.matches('[data-mix-add]')) {
      this.addBall(button.getAttribute('data-mix-add') || '');
    } else if (button.matches('[data-mix-notify]')) {
      this.toggleForm(button);
    } else if (button.matches('[data-mix-pump]')) {
      const variantId = button.getAttribute('data-mix-pump') || '';
      this.setPump(this.pump?.id === variantId ? null : variantId);
    } else if (button.matches('[data-mix-line-remove]')) {
      this.removeRow(Number(button.getAttribute('data-mix-line-remove')));
    } else if (button.matches('[data-mix-line-pump-remove]')) {
      this.setPump(null);
    } else if (button.matches('[data-mix-combo]')) {
      this.applyCombo(button.getAttribute('data-mix-combo') || '');
    } else if (button.matches('[data-mix-atc], [data-mix-bar-add]')) {
      this.addToCart();
    } else if (button.matches('[data-mix-replace-cancel]')) {
      this.endReplace();
    }
  };

  /** @param {Event} event */
  handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (target.matches('[data-mix-color]')) {
      const card = target.closest('[data-mix-card]');
      const key = card?.getAttribute('data-mix-card') || '';
      this.selection.set(key, target.value);
      if (card instanceof HTMLElement) this.updateCard(card);
    } else if (target.matches('[data-mix-pump-input]')) {
      this.setPump(target.checked && this.data.pump ? String(this.data.pump.id) : null);
    }
  };

  /** Arrow keys, Home and End move between the enabled tabs (WAI-ARIA tabs, automatic activation). @param {KeyboardEvent} event */
  handleTabKeys = (event) => {
    const keys = ['ArrowRight', 'ArrowLeft', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    const tabs = Array.from(this.querySelectorAll('[data-mix-tab]:not(:disabled)'));
    const current = tabs.findIndex((tab) => tab === document.activeElement);
    if (tabs.length === 0) return;
    let next = current;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;
    event.preventDefault();
    const tab = tabs[next];
    if (tab instanceof HTMLElement) {
      this.selectTab(tab.getAttribute('data-mix-tab') || '', false);
      tab.focus();
    }
  };

  /**
   * @param {string} family
   * @param {boolean} focusPanel
   */
  selectTab(family, focusPanel) {
    if (!family) return;
    this.querySelectorAll('[data-mix-tab]').forEach((tab) => {
      const active = tab.getAttribute('data-mix-tab') === family;
      tab.setAttribute('aria-selected', String(active));
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });
    const changed = this.activeFamily !== family;
    this.activeFamily = family;
    if (changed) this.renderCards();
    if (focusPanel) this.focusFirstAdd();
  }

  /** "+ Add another ball" on the tray: back to the cards, on the first add button. */
  goToCards() {
    const tabs = this.querySelector('[data-mix-tabs]');
    if (tabs instanceof HTMLElement) {
      const rect = tabs.getBoundingClientRect();
      if (rect.top < 0 || rect.bottom > window.innerHeight) {
        tabs.scrollIntoView({ block: 'start', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
      }
    }
    this.focusFirstAdd();
  }

  focusFirstAdd() {
    const panel = this.querySelector(`[data-mix-panel="${CSS.escape(this.activeFamily)}"]`);
    const target =
      panel?.querySelector('[data-mix-add]:not(:disabled), [data-mix-notify], [data-mix-pump]') ||
      this.querySelector('[data-mix-tab][aria-selected="true"]');
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  }

  /** A tap on a filled tray slot: the next ball picked replaces that one. @param {number} index */
  startReplace(index) {
    const selection = this.resolve(this.lines[index]);
    if (!selection) return;
    this.replaceIndex = index;
    const notice = this.querySelector('[data-mix-replace]');
    const text = this.querySelector('[data-mix-replace-text]');
    if (text) text.textContent = this.text('replaceHint', { n: index + 1 });
    if (notice instanceof HTMLElement) notice.hidden = false;
    const tab = this.tabOf(selection.product);
    if (tab && tab !== this.activeFamily) {
      this.selectTab(tab, false);
    }
    this.updateCards();
    this.goToCards();
  }

  endReplace() {
    const index = this.replaceIndex;
    this.replaceIndex = null;
    const notice = this.querySelector('[data-mix-replace]');
    if (notice instanceof HTMLElement) notice.hidden = true;
    this.updateCards();
    if (typeof index === 'number') this.tray?.focusSlot?.(index);
  }

  /** @param {string} variantId */
  addBall(variantId) {
    const selection = this.variants.get(variantId);
    if (!selection || !this.isOffered(selection.variant)) {
      this.setError(this.text('chooseAvailable'));
      return;
    }
    this.setError('');
    if (this.replaceIndex !== null && this.replaceIndex < this.lines.length) {
      this.lines[this.replaceIndex] = { variantId };
      this.replaceIndex = null;
      const notice = this.querySelector('[data-mix-replace]');
      if (notice instanceof HTMLElement) notice.hidden = true;
    } else {
      if (this.lines.length >= this.maxItems) return;
      this.lines.push({ variantId });
    }
    this.render({ announce: true });
  }

  /** @param {number} index */
  removeRow(index) {
    if (!(index >= 0 && index < this.lines.length)) return;
    this.lines.splice(index, 1);
    if (this.replaceIndex === index) this.endReplace();
    this.render({ announce: true });
    if (this.lines.length === 0) this.tray?.focusAdd?.();
    else this.tray?.focusSlot?.(Math.min(index, this.lines.length - 1));
  }

  /** The pump line: the tray's default pump (theme data) or an accessory picked on the Pumps tab. @param {string | null} variantId */
  setPump(variantId) {
    if (!variantId) {
      this.pump = null;
    } else if (this.data.pump && String(this.data.pump.id) === variantId) {
      this.pump = { id: variantId, p: Number(this.data.pump.p) || 0, name: this.data.pump.name || '' };
    } else {
      const selection = this.variants.get(variantId);
      if (!selection || !this.isOffered(selection.variant)) return;
      this.pump = { id: variantId, p: Number(selection.variant.p) || 0, name: selection.product.name };
    }
    this.render({ announce: true });
  }

  /** @param {string} spec - "variantId,variantId,…" */
  applyCombo(spec) {
    const ids = spec.split(',').filter((id) => this.isOffered(this.variants.get(id)?.variant));
    if (ids.length === 0) return;
    this.replaceIndex = null;
    const notice = this.querySelector('[data-mix-replace]');
    if (notice instanceof HTMLElement) notice.hidden = true;
    this.lines = ids.slice(0, this.maxItems).map((variantId) => ({ variantId }));
    this.render({ announce: true });
  }

  /**
   * The parked form for a variant that can't be bought: the product's waitlist form (waitlist stage) or its restock
   * form (live but sold out).
   * @param {Product} product
   * @param {Variant} variant
   */
  formFor(product, variant) {
    const kind = variant.s === 'waitlist' ? 'waitlist' : 'restock';
    return this.querySelector(`[data-mix-form="${CSS.escape(`${kind}-${product.id}`)}"]`);
  }

  /** "Notify me" on a card: the matching form (T11) moves into the card, tagged with the variant. @param {HTMLButtonElement} button */
  toggleForm(button) {
    const selection = this.variants.get(button.getAttribute('data-mix-notify') || '');
    if (!selection) return;
    const form = this.formFor(selection.product, selection.variant);
    const slot = button.closest('[data-mix-card]')?.querySelector('[data-mix-form-slot]');
    if (!(form instanceof HTMLElement) || !(slot instanceof HTMLElement)) return;
    const open = slot.contains(form);
    this.parkForms();
    this.querySelectorAll('[data-mix-notify]').forEach((other) => other.setAttribute('aria-expanded', 'false'));
    if (open) return;
    // Product-level tags stay (Shopify Email segments); the variant id is added so a restock mail can name the size.
    const tags = form.querySelector('input[name="contact[tags]"]');
    if (tags instanceof HTMLInputElement) {
      const base = tags.dataset.baseTags ?? tags.value;
      tags.dataset.baseTags = base;
      const kind = selection.variant.s === 'waitlist' ? 'waitlist' : 'restock';
      tags.value = `${base},${kind}-variant-${selection.variant.id}`;
    }
    slot.append(form);
    slot.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    const input = form.querySelector('input[type="email"]');
    if (input instanceof HTMLElement) input.focus({ preventScroll: true });
  }

  /** Every waitlist form back into its hidden parking div (before cards re-render, and when another card opens). */
  parkForms() {
    const park = this.querySelector('[data-mix-forms]');
    if (!park) return;
    this.querySelectorAll('[data-mix-form-slot] [data-mix-form]').forEach((form) => {
      const slot = form.parentElement;
      park.append(form);
      if (slot instanceof HTMLElement) slot.hidden = true;
    });
  }

  /* ---------- Cards ---------- */

  renderCards() {
    this.parkForms();
    this.querySelectorAll('[data-mix-panel]').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      const active = panel.getAttribute('data-mix-panel') === this.activeFamily;
      panel.hidden = !active;
      const list = panel.querySelector('[data-mix-sizes]');
      if (!list) return;
      if (!active) {
        list.replaceChildren();
        return;
      }
      const groups = this.sizesFor(this.activeFamily);
      list.replaceChildren(...groups.map((group) => this.card(group)));
    });
    this.updateCards();
  }

  /** @param {SizeGroup} group */
  card(group) {
    const isBall = BALL_FAMILIES.includes(this.activeFamily);
    const selected = this.variants.get(this.selectedIn(group));
    const { hex } = selected ? this.colorOf(selected.product, selected.variant) : { hex: undefined };
    // T48: a legacy card is titled with its own product title (which names its size), with the other unit below.
    const sizeLabel = group.cm && group.in && !group.legacy ? this.text('sizeDisplay', { in: group.in, cm: group.cm }) : group.name;
    const sizeAlt = group.cm && group.in ? this.text('sizeSecondary', { in: group.in, cm: group.cm }) : '';
    const classic = this.activeFamily === 'play' && group.cm === 127;

    const ballStyle = `--ball-size: ${this.ballSize(group.cm)}px; --ball-color: ${this.cardBallColor(group, hex)};`;

    const children = [
      el(
        'div',
        { class: 'bigens-mix__card-top' },
        group.legacy && group.img
          ? el('img', { class: 'bigens-mix__card-photo', src: group.img, alt: '', width: '64', height: '64', loading: 'lazy' })
          : el('span', { class: 'bigens-set-ball bigens-mix__card-ball', 'aria-hidden': 'true', style: ballStyle, 'data-mix-card-ball': '' }),
        el(
          'div',
          { class: 'bigens-mix__card-size' },
          el(
            'span',
            { class: 'bigens-mix__size' },
            sizeLabel,
            classic ? el('span', { class: 'bigens-mix__pill bigens-mix__pill--soft' }, this.strings.classic || '') : '',
          ),
          sizeAlt ? el('span', { class: 'bigens-mix__size-alt' }, sizeAlt) : '',
        ),
      ),
      // T48: a legacy card has no Play Ball who-for line; it links to its own page instead.
      group.legacy
        ? el('p', { class: 'bigens-mix__who' }, el('a', { class: 'bigens-mix__own-link', href: String(group.variants[0]?.variant.url || '').split('?')[0] }, `${this.strings.legacyLink || ''} →`))
        : el('p', { class: 'bigens-mix__who' }, isBall ? group.who : ''),
      el('p', { class: 'bigens-mix__price', 'data-mix-card-price': '' }),
    ];

    const swatches = this.swatchesFor(group);
    if (swatches.length > 1) {
      const name = `bigens-mix-color-${this.dataset.sectionId}-${group.key}`;
      const picked = this.selectedIn(group);
      children.push(
        el(
          'fieldset',
          { class: 'bigens-mix__colors' },
          el('legend', { class: 'visually-hidden' }, this.strings.colorLabel || ''),
          ...swatches.map(({ product, variant, label, color, variants }) => {
            const offered = this.isOffered(variant);
            return el(
              'label',
              { class: 'bigens-mix__swatch', title: label },
              el('input', {
                type: 'radio',
                class: 'bigens-mix__swatch-input',
                name,
                value: String(variant.id),
                'data-mix-color': '',
                checked: variants.some((candidate) => String(candidate.id) === picked),
              }),
              color.hex || !variant.img
                ? el('span', {
                    class: 'bigens-set-ball bigens-mix__swatch-ball',
                    'aria-hidden': 'true',
                    style: `--ball-color: ${color.hex || familyColor(product.family || this.activeFamily)};`,
                  })
                : el('img', { class: 'bigens-mix__swatch-ball bigens-mix__swatch-image', src: variant.img, alt: '', width: '28', height: '28', loading: 'lazy' }),
              el(
                'span',
                { class: 'visually-hidden' },
                offered || variant.s === 'waitlist' ? label : this.text('colorUnavailable', { color: label }),
              ),
            );
          }),
        ),
      );
    }

    children.push(el('p', { class: 'bigens-mix__ships', 'data-mix-card-ships': '', hidden: true }));
    children.push(el('div', { class: 'bigens-mix__actions', 'data-mix-actions': '' }));
    children.push(el('div', { class: 'bigens-mix__form-slot', 'data-mix-form-slot': '', hidden: true }));

    return el('li', { class: 'bigens-mix__card bigens-box--outline', 'data-mix-card': group.key }, ...children);
  }

  updateCards() {
    this.querySelectorAll('[data-mix-card]').forEach((card) => {
      if (card instanceof HTMLElement) this.updateCard(card);
    });
  }

  /** Price as it will be in the set, the ship note and the button follow the picked colour and the set. @param {HTMLElement} card */
  updateCard(card) {
    const key = card.getAttribute('data-mix-card') || '';
    const group = this.sizesFor(this.activeFamily).find((candidate) => candidate.key === key);
    if (!group) return;
    const selection = this.variants.get(this.selectedIn(group));
    if (!selection) return;
    const { product, variant } = selection;
    const isBall = BALL_FAMILIES.includes(this.activeFamily);
    const offered = this.isOffered(variant);
    const color = this.colorOf(product, variant);

    const ball = card.querySelector('[data-mix-card-ball]');
    if (ball instanceof HTMLElement) ball.style.setProperty('--ball-color', this.cardBallColor(group, color.hex));

    // Price: the per-ball price at the tier the set reaches once this ball is in it.
    const price = card.querySelector('[data-mix-card-price]');
    if (price && variant.s === 'waitlist') {
      // D55: no price before the owner sets one (waitlist).
      price.replaceChildren();
    } else if (price) {
      const count = this.replaceIndex === null ? this.lines.length + 1 : Math.max(1, this.lines.length);
      const percent = isBall && offered ? this.percentFor(count) : 0;
      const nodes = [el('strong', {}, this.formatMoney(percent > 0 ? this.discounted(variant, percent) : variant.p))];
      if (percent > 0) {
        nodes.push(el('s', {}, this.formatMoney(variant.p)));
        nodes.push(el('span', { class: 'bigens-mix__price-note' }, this.text('inSet', { count })));
      }
      price.replaceChildren(...nodes);
    }

    const ships = card.querySelector('[data-mix-card-ships]');
    if (ships instanceof HTMLElement) {
      let note = offered && variant.s === 'preorder' ? variant.n || this.strings.preorder || '' : '';
      if (!offered && isBall) note = variant.s === 'waitlist' ? this.strings.comingSoon || '' : this.strings.soldOutNote || '';
      ships.textContent = note;
      ships.dataset.tone = offered ? 'preorder' : 'out';
      ships.hidden = note === '';
    }

    card.classList.toggle('is-out', !offered);

    const actions = card.querySelector('[data-mix-actions]');
    if (!actions) return;
    const sizeLabel = this.sizeOf(product, variant);
    let button;
    if (!isBall) {
      const active = this.pump?.id === String(variant.id);
      button = el(
        'button',
        { type: 'button', class: 'bigens-mix__add', 'data-mix-pump': String(variant.id), 'aria-pressed': String(active), disabled: !offered },
        offered ? (active ? this.strings.removePump : this.strings.addPump) || '' : this.strings.soldOut || '',
      );
    } else if (!offered) {
      // T45 (AUD2 #6): no dead button. Waitlist → the waitlist form, sold out → the restock form (T11/T17), inline.
      const form = this.formFor(product, variant);
      const open = Boolean(form && card.contains(form));
      button = el(
        'button',
        {
          type: 'button',
          class: 'bigens-mix__add bigens-mix__add--notify',
          'data-mix-notify': String(variant.id),
          'aria-expanded': String(open),
          disabled: !form,
        },
        this.strings.notify || '',
      );
    } else {
      const full = this.replaceIndex === null && this.lines.length >= this.maxItems;
      const colorName = color.name;
      const label =
        this.replaceIndex !== null
          ? this.text('replaceWith', { n: this.replaceIndex + 1, color: colorName, size: sizeLabel })
          : colorName
            ? this.text('add', { color: colorName, size: sizeLabel })
            : this.text('addSingle', { size: sizeLabel });
      button = el(
        'button',
        { type: 'button', class: 'bigens-mix__add', 'data-mix-add': String(variant.id), disabled: full },
        label.replace(/\s+/g, ' ').trim(),
      );
    }
    const focused = actions.contains(document.activeElement);
    actions.replaceChildren(button);
    if (focused && !button.disabled) button.focus({ preventScroll: true });
  }

  /* ---------- Curated sets ---------- */

  /** Editor combos when the section has combo blocks, else three presets from what's on offer. */
  combos() {
    /** @type {Array<{ name: string, ids: string[], key?: string }>} */
    const list = [];
    const editorCombos = Array.isArray(this.data.combos) ? this.data.combos : [];
    editorCombos.forEach((/** @type {{ label: string, slots: string }} */ combo) => {
      const ids = String(combo.slots || '')
        .split(',')
        .map((slot) => {
          const [productId, variantId] = slot.split(':');
          if (variantId && variantId !== 'first') return variantId;
          const product = this.products.get(productId);
          const first = product?.variants.find((variant) => this.isOffered(variant));
          return first ? String(first.id) : '';
        });
      if (ids.length >= 2 && ids.every((id) => this.isOffered(this.variants.get(id)?.variant))) {
        list.push({ name: combo.label, ids });
      }
    });
    if (editorCombos.length > 0) return list;

    const play = this.offeredIn('play', 127);
    const hoppi = this.offeredIn('hoppi', 100);
    const pick = (/** @type {Variant[]} */ pool, /** @type {number} */ count) =>
      Array.from({ length: count }, (_, index) => String(pool[index % pool.length].id));
    if (play.length > 0 && hoppi.length > 0) list.push({ key: 'duo', name: this.strings.presetDuo || '', ids: [String(play[0].id), String(hoppi[0].id)] });
    if (play.length > 0) list.push({ key: 'trio', name: this.strings.presetTrio || '', ids: pick(play, 3) });
    if (play.length > 0) list.push({ key: 'four', name: this.strings.presetFour || '', ids: pick(play, 4) });
    return list.filter((combo) => combo.ids.length <= this.maxItems);
  }

  renderCombos() {
    const section = this.querySelector('[data-mix-combos]');
    const list = this.querySelector('[data-mix-combo-list]');
    if (!(section instanceof HTMLElement) || !list) return;
    const combos = this.combos();
    section.hidden = combos.length === 0;
    list.replaceChildren(
      ...combos.map(({ name, ids, key }) => {
        // M03 4.2: a set photo when one is uploaded under the preset's fixed name; else the row of balls only.
        const photo = key ? this.data.presetImages?.[key] : undefined;
        const balls = ids.map((id) => this.variants.get(id)).filter(Boolean);
        const variants = balls.map((ball) => ball?.variant);
        const { percent, total } = this.totals(variants);
        const regular = variants.reduce((sum, variant) => sum + (Number(variant?.p) || 0), 0);

        /** @type {Map<string, { count: number, label: string }>} */
        const counted = new Map();
        balls.forEach((ball) => {
          if (!ball) return;
          const label = [ball.product.short || ball.product.name, this.nameSize(ball.product, ball.variant), this.colorOf(ball.product, ball.variant).name]
            .filter(Boolean)
            .join(' ');
          const entry = counted.get(String(ball.variant.id)) || { count: 0, label };
          entry.count += 1;
          counted.set(String(ball.variant.id), entry);
        });
        const contents = Array.from(counted.values())
          .map(({ count, label }) => (count > 1 ? this.text('comboTimes', { count, ball: label }) : label))
          .join(' + ');
        const totalText = this.formatMoney(total);

        return el(
          'li',
          { class: 'bigens-mix__combo-item' },
          el(
            'button',
            {
              type: 'button',
              class: 'bigens-mix__combo bigens-box--outline',
              'data-mix-combo': ids.join(','),
              'aria-label': this.text('comboUse', { name, total: totalText }),
            },
            photo
              ? el('img', { class: 'bigens-mix__combo-image', src: photo, alt: '', width: '320', height: '240', loading: 'lazy' })
              : '',
            el(
              'span',
              { class: 'bigens-mix__combo-balls', 'aria-hidden': 'true' },
              ...balls.map((ball) =>
                ball
                  ? el('span', {
                      class: 'bigens-set-ball',
                      style: `--ball-color: ${this.colorOf(ball.product, ball.variant).hex || familyColor(ball.product.family)};`,
                    })
                  : '',
              ),
            ),
            el('span', { class: 'bigens-mix__combo-name' }, name),
            el('span', { class: 'bigens-mix__combo-contents' }, contents),
            percent > 0 ? el('span', { class: 'bigens-mix__pill' }, this.text('savePercent', { percent })) : '',
            el(
              'span',
              { class: 'bigens-mix__combo-price' },
              el('strong', {}, totalText),
              percent > 0 ? el('s', {}, this.formatMoney(regular)) : '',
            ),
          ),
        );
      }),
    );
  }

  /* ---------- Rendering ---------- */

  /** @param {{ announce?: boolean }} [options] */
  render({ announce = false } = {}) {
    if (!this.initialized) return;
    this.setError('');
    const count = this.lines.length;
    const balls = this.balls();
    const { percent, saving, total } = this.totals(balls, true);

    // The tray: slots, tier bar and the "one more ball" nudge (hidden when empty, at 4+ and at the maximum).
    let nudge = null;
    if (count > 0 && count < 4 && count < this.maxItems) {
      const next = [...balls, balls[balls.length - 1]];
      const nextTotals = this.totals(next);
      const prices = new Set(next.map((variant) => Number(variant?.p) || 0));
      nudge = prices.size === 1 ? { price: this.formatMoney(Math.ceil(nextTotals.total / next.length)) } : { percent: nextTotals.percent };
    }
    this.tray?.update?.({
      balls: this.lines.map((line) => this.ballView(line)),
      tiers: this.tiers,
      max: this.maxItems,
      nudge,
    });

    // Line items with struck prices, the pump line and the total row.
    const empty = this.querySelector('[data-mix-empty]');
    if (empty instanceof HTMLElement) empty.hidden = count > 0 || Boolean(this.pump);
    const list = this.querySelector('[data-mix-lines]');
    if (list instanceof HTMLElement) {
      const rows = this.lines.map((line, index) => {
        const selection = this.resolve(line);
        if (!selection) return '';
        const { product, variant } = selection;
        const meta = [product.name, this.nameSize(product, variant), this.colorOf(product, variant).name, variant.s === 'preorder' ? this.strings.preorder : '']
          .filter(Boolean)
          .join(' · ');
        return el(
          'li',
          { class: 'bigens-mix__line' },
          el('span', { class: 'bigens-mix__line-text' }, meta),
          el(
            'span',
            { class: 'bigens-mix__line-price' },
            percent > 0 ? el('s', {}, this.formatMoney(variant.p)) : '',
            this.formatMoney(percent > 0 ? this.discounted(variant, percent) : variant.p),
          ),
          el(
            'button',
            { type: 'button', class: 'bigens-mix__line-remove', 'data-mix-line-remove': String(index), 'aria-label': `${this.text('remove', { n: index + 1 })}: ${meta}` },
            el('span', { class: 'bigens-mix__line-remove-icon', 'aria-hidden': 'true' }),
          ),
        );
      });
      if (this.pump) {
        rows.push(
          el(
            'li',
            { class: 'bigens-mix__line bigens-mix__line--pump' },
            el('span', { class: 'bigens-mix__line-text' }, this.pump.name || this.strings.pumpLine || ''),
            el('span', { class: 'bigens-mix__line-price' }, this.formatMoney(this.pump.p)),
            el(
              'button',
              { type: 'button', class: 'bigens-mix__line-remove', 'data-mix-line-pump-remove': '', 'aria-label': this.strings.removePumpLine || '' },
              el('span', { class: 'bigens-mix__line-remove-icon', 'aria-hidden': 'true' }),
            ),
          ),
        );
      }
      if (count > 0) {
        const countText = count === 1 ? this.strings.countOne : this.text('countOther', { count });
        rows.push(
          el(
            'li',
            { class: 'bigens-mix__line bigens-mix__line--total' },
            el(
              'span',
              { class: 'bigens-mix__line-text' },
              saving > 0
                ? [el('span', {}, `${countText} · `), el('span', { class: 'bigens-mix__save' }, this.text('youSaveLower', { amount: this.formatMoney(saving) }))]
                : countText,
            ),
            el('span', { class: 'bigens-mix__line-price' }, this.formatMoney(total)),
          ),
        );
      }
      const nodes = rows.filter((row) => row !== '');
      list.replaceChildren(...nodes);
      list.hidden = nodes.length === 0;
    }

    const pumpInput = this.querySelector('[data-mix-pump-input]');
    if (pumpInput instanceof HTMLInputElement) pumpInput.checked = Boolean(this.pump && this.data.pump && this.pump.id === String(this.data.pump.id));

    // Add to cart with the total in a chip.
    const atc = this.querySelector('[data-mix-atc]');
    const chip = this.querySelector('[data-mix-atc-total]');
    if (atc instanceof HTMLButtonElement) atc.disabled = count === 0 && !this.pump;
    if (chip instanceof HTMLElement) {
      chip.textContent = this.formatMoney(total);
      chip.hidden = count === 0 && !this.pump;
    }

    this.updateNote();
    this.updateCards();
    this.updateBar();
    this.saveSet();

    if (announce) {
      const status = this.querySelector('[data-mix-status]');
      if (status) {
        if (count === 0) status.textContent = this.strings.statusEmpty || '';
        else {
          const parts = [this.text(count === 1 ? 'statusOne' : 'status', { count, total: this.formatMoney(total) })];
          if (saving > 0) parts.push(this.text('youSave', { amount: this.formatMoney(saving) }));
          status.textContent = parts.join('. ');
        }
      }
    }
  }

  /** One ball as the tray shows it. @param {{ variantId: string }} line */
  ballView(line) {
    const selection = this.resolve(line);
    if (!selection) return null;
    const { product, variant } = selection;
    const color = this.colorOf(product, variant);
    return {
      family: product.legacy ? '' : product.family,
      name: product.short || product.name,
      size: this.nameSize(product, variant),
      color: color.name,
      hex: color.hex,
      img: variant.img,
      preorder: variant.s === 'preorder',
      offered: this.isOffered(variant),
      fixed: false,
    };
  }

  /** "One parcel · ships when the Hoppi arrives (Nov) · 30-day returns" (D38: a month, never a promise). */
  updateNote() {
    const note = this.querySelector('[data-mix-note]');
    if (!note) return;
    const parts = [this.strings.oneParcel || ''];
    /** @type {Map<string, { name: string, dates: string[] }>} */
    const preorder = new Map();
    this.lines.forEach((line) => {
      const selection = this.resolve(line);
      if (!selection || selection.variant.s !== 'preorder') return;
      const key = selection.product.family || String(selection.product.id);
      const entry = preorder.get(key) || { name: selection.product.name, dates: [] };
      entry.dates.push(selection.variant.d || 'tbd');
      preorder.set(key, entry);
    });
    preorder.forEach(({ name, dates }) => {
      const dated = dates.filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date)).sort();
      const latest = dated[dated.length - 1];
      if (latest && dated.length === dates.length) {
        parts.push(this.text('noteShips', { family: name, month: this.formatDate(new Date(`${latest}T12:00:00`), { month: 'short' }) }));
      } else {
        parts.push(this.text('noteShipsTbd', { family: name }));
      }
    });
    parts.push(this.strings.returns || '');
    note.textContent = parts.filter(Boolean).join(' · ');
  }

  /** The phone bottom bar: mini balls, "n balls · total", "save x % · add 1 more for y %", "Add set". */
  updateBar = () => {
    const bar = this.bar;
    if (!(bar instanceof HTMLElement)) return;
    const count = this.lines.length;
    const { percent, total } = this.totals(this.balls(), true);

    const balls = bar.querySelector('[data-mix-bar-balls]');
    if (balls) {
      const views = this.lines.slice(0, 4).map((line) => this.ballView(line));
      const nodes = views.map((ball) =>
        el('span', { class: 'bigens-set-ball bigens-mix__bar-ball', style: `--ball-color: ${ball?.hex || familyColor(ball?.family || '')};` }),
      );
      for (let index = views.length; index < 4; index += 1) nodes.push(el('span', { class: 'bigens-mix__bar-empty' }));
      balls.replaceChildren(...nodes);
    }
    const title = bar.querySelector('[data-mix-bar-title]');
    if (title) title.textContent = count > 0 ? `${count === 1 ? this.strings.countOne : this.text('countOther', { count })} · ${this.formatMoney(total)}` : '';
    const sub = bar.querySelector('[data-mix-bar-sub]');
    if (sub) {
      const parts = [];
      if (percent > 0) parts.push(this.text('savePercentShort', { percent }));
      if (count > 0 && count < 4 && count < this.maxItems) parts.push(this.text('barMore', { percent: this.percentFor(count + 1) }));
      sub.textContent = parts.join(' · ');
    }

    const show = this.#mobile.matches && count > 0 && !this.trayVisible;
    bar.dataset.stuck = String(show);
    bar.setAttribute('aria-hidden', String(!show));
    const button = bar.querySelector('[data-mix-bar-add]');
    if (button instanceof HTMLButtonElement) button.tabIndex = show ? 0 : -1;
  };

  /* ---------- Cart ---------- */

  /** The same lines as the PDP builder's getCartItems(): grouped per variant, `_preorder` + "Expected shipping" on pre-order lines, the pump last. */
  cartItems() {
    /** @type {Map<string, { id: number, quantity: number, properties?: Record<string, string> }>} */
    const grouped = new Map();
    for (const line of this.lines) {
      const selection = this.resolve(line);
      if (!selection || !this.isOffered(selection.variant)) return null;
      const key = String(selection.variant.id);
      const entry = grouped.get(key) || { id: Number(selection.variant.id), quantity: 0, properties: this.lineProperties(selection.variant) };
      entry.quantity += 1;
      grouped.set(key, entry);
    }
    const items = Array.from(grouped.values()).map((item) => (item.properties ? item : { id: item.id, quantity: item.quantity }));
    if (this.pump) items.push({ id: Number(this.pump.id), quantity: 1 });
    return items;
  }

  /** @param {Variant} variant */
  lineProperties(variant) {
    if (variant.s !== 'preorder') return undefined;
    /** @type {Record<string, string>} */
    const properties = { _preorder: variant.d || 'tbd' };
    if (variant.dl && this.strings.propertyLabel) properties[this.strings.propertyLabel] = variant.dl;
    return properties;
  }

  async addToCart() {
    if (this.busy) return;
    const items = this.cartItems();
    if (!items) {
      this.setError(this.text('chooseAvailable'));
      return;
    }
    if (items.length === 0) return;

    const buttons = Array.from(this.querySelectorAll('[data-mix-atc], [data-mix-bar-add]'));
    this.busy = true;
    buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));
    this.setError('');

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    const count = this.lines.length;

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map(({ id, quantity }) => ({ merchandiseId: String(id), quantity })),
        promise: deferred.promise,
      }),
    );

    try {
      const routes = /** @type {any} */ (window).Theme?.routes || {};
      const body = JSON.stringify({ items, sections: sectionIds.join(','), sections_url: window.location.pathname });
      const response = await fetch(routes.cart_add_url || '/cart/add.js', fetchConfig('json', { body })).then((r) => r.json());
      const cart = await fetch(`${routes.cart_url || '/cart'}.js`).then((r) => r.json());

      if (response.status) {
        const message = response.description || response.message || this.strings.cartError || '';
        this.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: this.localName },
        });
        this.setError(message);
        return;
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
          sections: response.sections,
          source: this.localName,
        },
      });

      // The set is in the cart now: start fresh (and forget it in localStorage).
      this.lines = [];
      this.pump = null;
      this.replaceIndex = null;
      this.render();
      const status = this.querySelector('[data-mix-status]');
      if (status) status.textContent = count === 1 ? this.text('statusAddedOne') : this.text('statusAdded', { count });
      const label = this.querySelector('[data-mix-atc-label]');
      if (label) {
        label.textContent = this.strings.added || '';
        clearTimeout(this.addedTimer);
        this.addedTimer = setTimeout(() => {
          label.textContent = this.strings.atcTotal || '';
        }, 2000);
      }
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.setError(this.strings.cartError || '');
    } finally {
      this.busy = false;
      buttons.forEach((button) => button.removeAttribute('aria-busy'));
    }
  }

  /* ---------- Helpers ---------- */

  /** @param {string} message */
  setError(message) {
    const error = this.querySelector('[data-mix-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = message === '';
  }

  /**
   * @param {string} key
   * @param {Record<string, string | number>} [values] - replaces `[name]` placeholders
   */
  text(key, values = {}) {
    let text = String(this.strings[key] || '');
    Object.entries(values).forEach(([name, value]) => {
      text = text.replaceAll(`[${name}]`, String(value));
    });
    return text;
  }

  /**
   * @param {Date} date
   * @param {Intl.DateTimeFormatOptions} options
   */
  formatDate(date, options) {
    const locale = this.dataset.locale || document.documentElement.lang || undefined;
    try {
      return new Intl.DateTimeFormat(locale, options).format(date);
    } catch {
      return new Intl.DateTimeFormat(undefined, options).format(date);
    }
  }

  /** @param {number} cents */
  formatMoney(cents) {
    const moneyFormat = this.data.moneyFormat || '{{amount}}';
    const placeholder = moneyFormat.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!placeholder) return String(cents);

    /** @type {Record<string, [number, string, string]>} */
    const formats = {
      amount: [2, ',', '.'],
      amount_no_decimals: [0, ',', '.'],
      amount_with_comma_separator: [2, '.', ','],
      amount_no_decimals_with_comma_separator: [0, '.', ','],
      amount_with_apostrophe_separator: [2, "'", '.'],
      amount_with_space_separator: [2, ' ', ','],
      amount_no_decimals_with_space_separator: [0, ' ', ','],
    };
    const [precision, thousandsSeparator, decimalSeparator] = formats[placeholder[1]] || formats.amount;
    const [integerPart, decimalPart] = (Number(cents) / 100).toFixed(precision).split('.');
    const groupedInteger = (integerPart || '0').replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);
    const amount = decimalPart ? `${groupedInteger}${decimalSeparator}${decimalPart}` : groupedInteger;
    const decoder = document.createElement('template');
    decoder.innerHTML = moneyFormat.replace(placeholder[0], amount);
    return decoder.content.textContent?.trim() || amount;
  }
}

/**
 * Tiny DOM builder (text is always set as text, never as HTML).
 * @param {string} tag
 * @param {Record<string, string | boolean>} attributes
 * @param {...(Node | string | Array<Node | string>)} children
 */
function el(tag, attributes = {}, ...children) {
  const node = document.createElement(tag);
  Object.entries(attributes).forEach(([name, value]) => {
    if (value === false) return;
    if (name === 'checked' || name === 'disabled' || name === 'hidden') {
      /** @type {any} */ (node)[name] = value;
      return;
    }
    node.setAttribute(name, value === true ? '' : String(value));
  });
  children.flat().forEach((child) => {
    if (child !== '' && child != null) node.append(child);
  });
  return node;
}

/** @param {string} family */
function familyColor(family) {
  return family ? `var(--bigens-family-${family})` : 'var(--bigens-black)';
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

if (!customElements.get('bigens-mix-match')) {
  customElements.define('bigens-mix-match', BigensMixMatch);
}
